/**
 * Cross-platform keyboard shortcut utilities.
 *
 * Provides centralized platform detection + human-readable shortcut labels
 * for Mac / Windows / Linux / Mobile contexts.
 *
 * Usage:
 *   shortcutLabel("Mod+S")          → "⌘S" (Mac) / "Ctrl+S" (Win)
 *   shortcutLabel("Mod+Shift+D")    → "⇧⌘D" (Mac) / "Ctrl+Shift+D" (Win)
 *   shortcutLabel("Mod+Backspace")  → "⌘⌫" (Mac) / "Del" (Win)
 */

// ---------------------------------------------------------------------------
// Platform detection
// ---------------------------------------------------------------------------

let _isMac: boolean | null = null

/** True on macOS / iPad / iPhone (also supports SSR). */
export function isMac(): boolean {
  if (_isMac !== null) return _isMac
  if (typeof navigator === "undefined") return false
  _isMac =
    /Mac|iPhone|iPad|iPod/.test(navigator.platform) ||
    /Macintosh/.test(navigator.userAgent)
  return _isMac
}

/** True on touch-primary devices without physical keyboard. */
export function isMobile(): boolean {
  if (typeof navigator === "undefined") return false
  return /Android|iPhone|iPad|iPod/.test(navigator.userAgent) && !isExternalKeyboard()
}

/**
 * Heuristic: if a Bluetooth keyboard is connected we still want shortcuts.
 * We detect this by checking if the device recently produced a real KeyboardEvent.
 */
let _externalKeyboard = false
export function markExternalKeyboard(): void {
  _externalKeyboard = true
}
function isExternalKeyboard(): boolean {
  return _externalKeyboard
}

// ---------------------------------------------------------------------------
// Symbol maps
// ---------------------------------------------------------------------------

const MAC_SYMBOLS: Record<string, string> = {
  mod: "⌘",
  ctrl: "⌃",
  alt: "⌥",
  shift: "⇧",
  enter: "↩",
  backspace: "⌫",
  delete: "⌦",
  escape: "⎋",
  tab: "⇥",
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  space: "␣",
}

const WIN_SYMBOLS: Record<string, string> = {
  mod: "Ctrl",
  ctrl: "Ctrl",
  alt: "Alt",
  shift: "Shift",
  enter: "Enter",
  backspace: "Backspace",
  delete: "Del",
  escape: "Esc",
  tab: "Tab",
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  space: "Space",
}

// ---------------------------------------------------------------------------
// shortcutLabel()
// ---------------------------------------------------------------------------

/**
 * Convert a canonical shortcut string to a human-readable label.
 *
 * Canonical format: `"Mod+Shift+K"` (case-insensitive).
 * On Mac:     `"⇧⌘K"`  (modifiers concatenated tightly)
 * On Windows: `"Ctrl+Shift+K"`  (separated by +)
 *
 * Special: `"Mod+Backspace"` on Windows becomes `"Del"`.
 */
export function shortcutLabel(shortcut: string): string {
  // Windows special: Mod+Backspace → Del
  if (!isMac() && /^mod\+backspace$/i.test(shortcut)) return "Del"

  const symbols = isMac() ? MAC_SYMBOLS : WIN_SYMBOLS
  const sep = isMac() ? "" : "+"

  const parts = shortcut.split("+").map((p) => p.trim())
  const mapped = parts.map((p) => symbols[p.toLowerCase()] ?? p.toUpperCase())

  return mapped.join(sep)
}

/**
 * Check if the current modifier key is pressed for a KeyboardEvent.
 * Uses metaKey on Mac, ctrlKey on Windows/Linux.
 */
export function isModKey(e: KeyboardEvent | React.KeyboardEvent): boolean {
  return isMac() ? e.metaKey : e.ctrlKey
}
